import React, { Fragment, useState, useRef, useEffect, useCallback } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import styles from './styles.module.scss';
import classNames from 'classnames';
import _ from 'lodash';
import { CCMClientSettingsService } from './services/CCMClientSettingsService';
import { CCMClientSettingsConfig } from './config/CCMClientSettingsConfig';
import { ProgramsOverviewComponent } from './components/programs-overview/ProgramsOverviewComponent';
import { ClientOverviewComponent } from './components/client-overview/ClientOverviewComponent';
import { ClientCodeStatusComponent } from './components/client-code-status/ClientCodeStatusComponent';
import { SubmitActionsComponent } from './components/submit-actions/SubmitActionsComponent';
import { ValidationErrorsComponent } from './components/validation-errors/ValidationErrorsComponent';
import { SyncStatusModalComponent } from './components/sync-status-modal/SyncStatusModalComponent';
import { SubmitActionsDisplayComponent } from './components/submit-actions/submit-actions-display/SubmitActionsDisplayComponent';
import toastr from '../toastr';

export const CCMClientSettingsView = ({ defaultClientCode }) => {
  const DEFAULT_STATE_CODE = 0;
  const {
    STATE_CODES: {
      EXISTS_IN_SF_NOT_ADMIN,
      CODE_IMPORTED_SUCCESFULLY,
      CODE_ALREADY_IMPORTED,
      CODE_NOT_IN_ADMIN_SF,
      CODE_IS_NOT_NET_NEW,
      MULTIPLE_OVERVIEWS_EXIST,
      ALL_OVERVIEWS_INACTIVE,
      SALESFORCE_CONNECTION_ERROR,
      CLIENT_IS_COHORT_SCENARIO
    },
    STATE_DISPLAY_TEXT: {
      EXISTS_IN_SF_NOT_ADMIN: EXISTS_IN_SF_NOT_ADMIN_TEXT,
      CODE_IMPORTED_SUCCESFULLY: CODE_IMPORTED_SUCCESFULLY_TEXT,
      CODE_ALREADY_IMPORTED: CODE_ALREADY_IMPORTED_TEXT,
      CODE_NOT_IN_ADMIN_SF: CODE_NOT_IN_ADMIN_SF_TEXT,
      CODE_IS_NOT_NET_NEW: CODE_IS_NOT_NET_NEW_TEXT,
      MULTIPLE_OVERVIEWS_EXIST: MULTIPLE_OVERVIEWS_EXIST_TEXT,
      ALL_OVERVIEWS_INACTIVE: ALL_OVERVIEWS_INACTIVE_TEXT,
      SALESFORCE_CONNECTION_ERROR: SALESFORCE_CONNECTION_ERROR_TEXT,
      CLIENT_IS_COHORT_SCENARIO: CLIENT_IS_COHORT_SCENARIO_TEXT
    },
    SYNC_STATUS_MESSAGE_VALIDATION_RESPONSE: { SUBMITTED, SUCCESSFUL, SUCCESS, FAILED },
    CONFIRM_SUBMIT,
    FIVE_SECONDS,
    CLIENT_CODE_REGEX,
    CLIENT_CODE_ERR_TEXT,
    MAX_GET_VALIDATION_ATTEMPTS,
    GET_VALIDATION_MESSAGES_ERROR
  } = CCMClientSettingsConfig;

  const [searchText, setSearchText] = useState(defaultClientCode);
  const [currentClientCode, setCurrentClientCode] = useState('');
  const [clientStatusDisplayText, setClientStatusDisplayText] = useState('');
  const [clientOverview, setClientOverview] = useState();
  const [programOverviews, setProgramOverviews] = useState();
  const [lastSyncWithSalesForceInfo, setLastSyncWithSalesForceInfo] = useState();
  const [downstreamSyncState, setDownstreamSyncState] = useState('');
  const [downstreamJsonDetails, setDownstreamJsonDetails] = useState();
  const [lastSubmissionInfo, setLastSubmissionInfo] = useState();
  const [validationErrors, setValidationErrors] = useState([]);
  const [clientCodeStatus, setClientCodeStatus] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [clientSearchError, setClientSearchError] = useState(null);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [loadingValidationErrors, setLoadingValidationErrors] = useState(false);
  const [isSyncStatusModalOpen, setIsSyncStatusModalOpen] = useState(false);
  const [syncStateError, setSyncStateError] = useState(null);
  const [isSyncingWithSalesForce, setIsSyncingWithSalesForce] = useState(false);
  const [numValidationAttempts, setNumValidationAttempts] = useState(0);
  const statusCheckInterval = useRef(null);
  const shouldDisableSearch =
    isSearching ||
    isImporting ||
    !_.defaultTo(searchText, '').trim().match(CLIENT_CODE_REGEX) ||
    _.defaultTo(searchText, '').trim().length < 2;

  const updateUpstreamClientContractState = (upstreamContract = null) => {
    if (!_.isNil(upstreamContract)) {
      setClientOverview(upstreamContract.client_contract_json.client_overview);
      const programs = upstreamContract.client_contract_json.program_overviews;
      setProgramOverviews(programs);
      setLastSyncWithSalesForceInfo({
        sync_by: upstreamContract.sync_by_name,
        sync_date: upstreamContract.sync_dt
      });
    } else {
      setLastSyncWithSalesForceInfo(null);
      setClientOverview(null);
      setProgramOverviews([]);
    }
  };

  const updateDownStreamClientContractState = (downstreamContract = null) => {
    if (!_.isNil(downstreamContract)) {
      setDownstreamJsonDetails(downstreamContract.client_contract_json);
      setDownstreamSyncState(downstreamContract.sync_status_message_json);
      setLastSubmissionInfo({
        sync_by: downstreamContract.sync_by_name,
        sync_date: downstreamContract.sync_dt
      });

      setValidationErrors(_.get(downstreamContract.sync_status_message_json, 'error_list', []));
      setDownstreamSyncState(_.get(downstreamContract.sync_status_message_json, 'status', ''));
    } else {
      setDownstreamJsonDetails(null);
      setDownstreamSyncState('');
      setLastSubmissionInfo(null);
      setValidationErrors([]);
    }
  };

  const resetAllClientState = () => {
    updateUpstreamClientContractState(null);
    updateDownStreamClientContractState(null);

    setValidationErrors([]);
    setLoadingValidationErrors(false);
    setSyncStateError(null);
    setClientCodeStatus(0);
    setClientSearchError(null);
    setLastSubmissionInfo(null);
    clearInterval(statusCheckInterval.current);
    setNumValidationAttempts(0);
  };

  const getClientStatusDisplayText = code => {
    const statusCodeToTextMapping = {
      [EXISTS_IN_SF_NOT_ADMIN]: EXISTS_IN_SF_NOT_ADMIN_TEXT,
      [CODE_IMPORTED_SUCCESFULLY]: CODE_IMPORTED_SUCCESFULLY_TEXT,
      [CODE_ALREADY_IMPORTED]: CODE_ALREADY_IMPORTED_TEXT,
      [CODE_NOT_IN_ADMIN_SF]: CODE_NOT_IN_ADMIN_SF_TEXT,
      [CODE_IS_NOT_NET_NEW]: CODE_IS_NOT_NET_NEW_TEXT,
      [MULTIPLE_OVERVIEWS_EXIST]: MULTIPLE_OVERVIEWS_EXIST_TEXT,
      [SALESFORCE_CONNECTION_ERROR]: SALESFORCE_CONNECTION_ERROR_TEXT,
      [CLIENT_IS_COHORT_SCENARIO]: CLIENT_IS_COHORT_SCENARIO_TEXT,
      [ALL_OVERVIEWS_INACTIVE]: ALL_OVERVIEWS_INACTIVE_TEXT
    };

    return _.get(statusCodeToTextMapping, code, '');
  };

  const handleSearchChange = e => {
    e.preventDefault();

    const sText = _.get(e, 'target.value', '').trim();

    setClientSearchError(() => (sText === '' || sText.match(CLIENT_CODE_REGEX) ? null : CLIENT_CODE_ERR_TEXT));
    setSearchText(sText);
  };

  const handleSearchClientCode = () => {
    setIsSearching(true);
    setIsSyncingWithSalesForce(true);
    if (searchText === currentClientCode) {
      setCurrentClientCode('');
    }
    resetAllClientState();

    CCMClientSettingsService.searchClientCode(searchText).then(response => {
      const {
        status: ccStatus = DEFAULT_STATE_CODE,
        contract: contract = null,
        errorMessage: statusCheckErrorMessage = null
      } = response;

      if (!_.isEmpty(statusCheckErrorMessage)) {
        toastr.error('Search client code failed', 'Failure');
      }

      updateUpstreamClientContractState(contract && contract.upstream_contract);
      updateDownStreamClientContractState(contract && contract.downstream_contract);

      setCurrentClientCode(searchText);
      setClientCodeStatus(ccStatus);
      setClientStatusDisplayText(getClientStatusDisplayText(ccStatus));

      setClientSearchError(statusCheckErrorMessage);
      setIsSearching(false);
      setIsSyncingWithSalesForce(false);
    });
  };

  const handleNoBtn = () => {
    setSearchText('');
    resetAllClientState();
  };

  const handleImportClient = () => {
    setIsImporting(true);

    CCMClientSettingsService.importFromSalesForce(currentClientCode).then(response => {
      const { contract: contract = null, errorMessage: statusCheckErrorMessage = null } = response;

      if (!_.isEmpty(statusCheckErrorMessage)) {
        toastr.error('Import from salesforce failed', 'Failure');
      }

      updateUpstreamClientContractState(contract && contract.upstream_contract);
      updateDownStreamClientContractState(contract && contract.downstream_contract);

      setCurrentClientCode(searchText);
      setClientCodeStatus(CODE_ALREADY_IMPORTED);
      setClientStatusDisplayText(getClientStatusDisplayText(CODE_IMPORTED_SUCCESFULLY));

      setClientSearchError(statusCheckErrorMessage);
      setIsSyncingWithSalesForce(false);
      setIsImporting(false);
    });
  };

  const handleSyncWithSalesForce = async () => {
    setIsSyncingWithSalesForce(true);
    const syncWithSFData = await CCMClientSettingsService.syncContract(currentClientCode);
    setIsSyncingWithSalesForce(false);

    if (_.isUndefined(syncWithSFData)) return;

    const {
      data: { contract: contract }
    } = syncWithSFData;

    updateUpstreamClientContractState(contract && contract.upstream_contract);
  };

  const handleValidationAttemptsExceeded = clientCode => {
    CCMClientSettingsService.updateValidationMessages(clientCode, GET_VALIDATION_MESSAGES_ERROR).then(() => {
      refreshValidationErrors(clientCode);
      clearInterval(statusCheckInterval.current);
      setLoadingValidationErrors(false);
      setNumValidationAttempts(0);
      toastr.error('Failed to retrieve validation messages', 'Failure');
    });
  };

  const refreshValidationErrors = clientCode => {
    CCMClientSettingsService.getContractDetails({
      clientId: clientCode,
      flowDirection: 'downstream'
    }).then(({ data: { contract, errorMessage } }) => {
      if (!_.isNil(errorMessage)) {
        toastr.error('Get contract details failed', 'Failure');
        setLoadingValidationErrors(false);
        setValidationErrors([]);
        setSyncStateError(errorMessage);
      }
      const { status: syncState, error_list: syncErrors } = contract.sync_status_message_json;
      if (!_.isNil(contract) && syncErrors && syncErrors.length) {
        setDownstreamSyncState(syncState);
        setDownstreamJsonDetails(contract);
        setValidationErrors(syncErrors);
      }
    });
  };

  const getValidationErrors = useCallback(() => {
    let syncState = '';
    CCMClientSettingsService.getContractDetails({
      clientId: currentClientCode,
      flowDirection: 'downstream'
    }).then(({ data: { contract, errorMessage } }) => {
      if (!_.isNil(errorMessage)) {
        toastr.error('Get contract details failed', 'Failure');

        setLoadingValidationErrors(false);
        setValidationErrors([]);
        setSyncStateError(errorMessage);
      }

      if (!_.isNil(contract)) {
        syncState = contract.sync_status_message_json.status;
        setNumValidationAttempts(prevAttempts => prevAttempts + 1);
        if (syncState !== SUBMITTED) {
          setNumValidationAttempts(0);
          clearInterval(statusCheckInterval.current);
          setLoadingValidationErrors(false);
          setValidationErrors(contract.sync_status_message_json.error_list);
          setSyncStateError(null);

          updateDownStreamClientContractState(contract);

          if ([SUCCESS, SUCCESSFUL].includes(syncState)) {
            toastr.success('Submission processed successfully', 'Success');
          } else if (syncState === FAILED) {
            toastr.error('Submission failed', 'Failed');
          }

          setDownstreamSyncState(syncState);
          setDownstreamJsonDetails(contract);
        }
      }
    });
  }, [currentClientCode]);

  useEffect(() => {
    if (_.defaultTo(defaultClientCode, '').trim() !== '') {
      handleSearchClientCode();
    }

    return () => {
      clearInterval(statusCheckInterval.current);
    };
  }, []);

  useEffect(() => {
    if (currentClientCode && numValidationAttempts >= MAX_GET_VALIDATION_ATTEMPTS) {
      handleValidationAttemptsExceeded(currentClientCode);
    }
  }, [currentClientCode, numValidationAttempts]);

  const closeSyncStatusModal = () => {
    setIsSyncStatusModalOpen(false);
  };

  const handleSubmitContract = async ({ action = '' }) => {
    const submitAction = () => {
      setIsSubmitLoading(true);
      setDownstreamSyncState(SUBMITTED);

      CCMClientSettingsService.submitContract(currentClientCode)
        .then(res => {
          const {
            data: { contract: contract }
          } = res;

          setLoadingValidationErrors(true);
          statusCheckInterval.current = setInterval(getValidationErrors, FIVE_SECONDS);

          setIsSubmitLoading(false);
        })
        .catch(err => {
          //TODO figure out where translations are defined and use translate instead
          toastr.error('Submit contract failed', 'Failure');

          setIsSubmitLoading(false);
        });
    };

    if (action === CONFIRM_SUBMIT) {
      submitAction();
    } else {
      if (downstreamSyncState === SUBMITTED) {
        setIsSyncStatusModalOpen(true);
      } else {
        submitAction();
      }
    }
  };

  return (
    <Fragment>
      <div>
        <h2>CCM Client Settings</h2>
        <div className={classNames(styles.searchContainer)}>
          <p>Search CCM client settings by client code</p>
          <div>
            <input
              name="searchCCMClientCodeSettings"
              type="text"
              value={searchText}
              placeholder="Search Client Codes"
              className={classNames(styles.searchField, {})}
              onChange={handleSearchChange}
              onKeyDown={event =>
                event.key === 'Enter' && searchText && !shouldDisableSearch && handleSearchClientCode()
              }
            />
            <button
              id="searchClientBtn"
              className={classNames('ml-2', styles.regularBtn)}
              onClick={handleSearchClientCode}
              disabled={shouldDisableSearch}
            >
              {isSearching ? 'SEARCHING...' : 'SEARCH'}
            </button>
          </div>
        </div>

        <ClientCodeStatusComponent
          id="ClientCodeStatusComponent"
          clientCode={currentClientCode}
          clientCodeStatus={clientCodeStatus}
          clientStatusDisplayText={clientStatusDisplayText}
          isImporting={isImporting}
          handleYesBtn={handleImportClient}
          handleNoBtn={handleNoBtn}
          error={clientSearchError}
        />
      </div>
      {clientOverview && !isSyncingWithSalesForce && (
        <ClientOverviewComponent id="ClientOverviewComponent" overview={clientOverview} />
      )}
      {programOverviews && !isSyncingWithSalesForce && (
        <ProgramsOverviewComponent id="ProgramsOverviewComponent" programOverviews={programOverviews} />
      )}
      <SyncStatusModalComponent
        title="Sync status warning"
        isOpen={isSyncStatusModalOpen}
        modalActions={{ submitClick: handleSubmitContract, closeModal: closeSyncStatusModal }}
      />
      <ValidationErrorsComponent
        errors={validationErrors}
        isLoading={loadingValidationErrors}
        loadingError={syncStateError}
        submitStatus={downstreamSyncState}
      />
      {lastSyncWithSalesForceInfo && (
        <SubmitActionsDisplayComponent
          syncWithSalesForceInfo={lastSyncWithSalesForceInfo}
          submissionInfo={lastSubmissionInfo}
        />
      )}
      {!!clientOverview && (
        <SubmitActionsComponent
          handleSubmitContract={handleSubmitContract}
          isSubmitLoading={isSubmitLoading}
          downstreamJsonDetails={downstreamJsonDetails}
          isSyncingWithSalesForce={isSyncingWithSalesForce}
          handleSyncWithSalesForce={handleSyncWithSalesForce}
          disableViewYamlSettings={!downstreamJsonDetails}
          submitStatus={downstreamSyncState}
        />
      )}
    </Fragment>
  );
};

export const CCMClientSettingsContainer = props => (
  <Router>
    <CCMClientSettingsView defaultClientCode={_.get(props, 'defaultClientCode', '')} />
  </Router>
);
